import { sendGTMEvent } from '@next/third-parties/google';
import {
  createVideoInteractionGTMEvent,
  VideoInteractionType,
} from 'lib/utils/gtm-utils';

export const VIDEO_PROGRESS_MILESTONES = [25, 50, 75, 100] as const;

export type VideoProgressMilestone = (typeof VIDEO_PROGRESS_MILESTONES)[number];

export type VideoAnalyticsMeta = {
  videoTitle: string;
  videoId: string;
  pageUrl?: string;
};

/**
 * Returns progress milestones that have been reached but not yet reported
 * for the current playback session.
 */
export const getUnfiredMilestones = (
  percent: number,
  alreadyFired: ReadonlySet<number>,
  milestones: readonly number[] = VIDEO_PROGRESS_MILESTONES
): number[] =>
  milestones.filter((milestone) => percent >= milestone && !alreadyFired.has(milestone));

export const sendVideoAnalyticsEvent = (
  interactionType: VideoInteractionType,
  meta: VideoAnalyticsMeta,
  videoPercent?: number
): void => {
  if (typeof window === 'undefined') return;

  const gtmEvent = createVideoInteractionGTMEvent({
    videoTitle: meta.videoTitle,
    videoId: meta.videoId,
    pageUrl: meta.pageUrl || window.location.href,
    interactionType,
    videoPercent,
  });

  sendGTMEvent({ ...gtmEvent });
};

/** Creates a session tracker that fires each milestone at most once until reset. */
export const createMilestoneSession = (
  milestones: readonly number[] = VIDEO_PROGRESS_MILESTONES
) => {
  const fired = new Set<number>();

  return {
    reset: () => {
      fired.clear();
    },
    /** Returns newly reached milestones and marks them as fired. */
    consume: (percent: number): number[] => {
      const reached = getUnfiredMilestones(percent, fired, milestones);
      reached.forEach((milestone) => fired.add(milestone));
      return reached;
    },
  };
};
